<template>
  <div class="ip-paso3">

    <!-- ═══ Barra controles globales ═══ -->
    <div class="gc-bar">
      <div class="gc-controls">
        <div class="gc-control-wrap gc-control-wrap--lg">
          <label class="gc-label">
            <v-icon x-small class="mr-1" style="color:#5F6368">mdi-barcode</v-icon>
            Estrategia codigo
          </label>
          <v-select
            :value="modoCodigoBarras"
            :items="opcionesModoCodigoBarras"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="gc-select"
            @input="onModoCodigoBarras"
          />
        </div>
        <div class="gc-control-wrap">
          <label class="gc-label">
            <v-icon x-small class="mr-1" style="color:#5F6368">mdi-percent</v-icon>
            IVA global
          </label>
          <v-select
            v-model.number="ivaPorcentajeGlobal"
            :items="ivaOptions"
            dense
            outlined
            hide-details
            class="gc-select gc-select--sm"
          />
        </div>
        <div class="gc-control-wrap">
          <label class="gc-label">
            <v-icon x-small class="mr-1" style="color:#5F6368">mdi-tag-outline</v-icon>
            Desc. %
          </label>
          <v-text-field
            v-model.number="descuentoProveedorGlobal"
            type="number"
            :min="0"
            :max="100"
            suffix="%"
            dense
            outlined
            hide-details
            class="gc-input gc-input--sm"
          />
        </div>
        <div class="gc-control-wrap">
          <label class="gc-label">
            <v-icon x-small class="mr-1" style="color:#5F6368">mdi-trending-up</v-icon>
            Margen %
          </label>
          <v-text-field
            v-model.number="porcentajeSobreCostoGlobal"
            type="number"
            :min="0"
            suffix="%"
            dense
            outlined
            hide-details
            class="gc-input gc-input--sm"
          />
        </div>
        <div class="gc-control-wrap">
          <label class="gc-label">
            <v-icon x-small class="mr-1" style="color:#5F6368">mdi-currency-usd</v-icon>
            Redondeo
          </label>
          <v-select
            v-model="tipoRedondeoGlobal"
            :items="opcionesRedondeo"
            dense
            outlined
            hide-details
            class="gc-select"
          />
        </div>
      </div>
    </div>

    <!-- ═══ Resumen validacion ═══ -->
    <div
      v-if="validacion"
      class="val-summary"
      :class="{
        'val-summary--error': totalErrores > 0,
        'val-summary--warn': totalErrores === 0 && totalAdvertencias > 0,
        'val-summary--ok': totalErrores === 0 && totalAdvertencias === 0
      }"
    >
      <v-icon small class="mr-2">
        {{ totalErrores > 0 ? 'mdi-alert-circle-outline' : totalAdvertencias > 0 ? 'mdi-information-outline' : 'mdi-check-circle-outline' }}
      </v-icon>
      <span v-if="totalErrores > 0">
        <strong>{{ totalErrores }} error(es)</strong> encontrado(s) — corregi los datos antes de continuar.
        <span v-if="totalAdvertencias > 0"> + {{ totalAdvertencias }} aviso(s).</span>
      </span>
      <span v-else-if="totalAdvertencias > 0">
        {{ totalAdvertencias }} aviso(s): se crearan nuevas marcas, modelos o tamanios.
      </span>
      <span v-else>
        Todos los datos son validos.
      </span>
    </div>

    <!-- ═══ Tabla ═══ -->
    <div v-if="productosConPrecio.length === 0" class="empty-state">
      <v-icon size="32" color="#BDBDBD">mdi-package-variant</v-icon>
      <p class="empty-text">No hay productos para configurar.</p>
    </div>

    <div v-else class="table-wrapper">
      <div class="table-scroll">
        <table class="rev-table">
          <thead>
            <tr>
              <th class="rth rth-fixed">Estado</th>
              <th v-if="mostrarColCodigoProveedor" class="rth">
                {{ $t("importarProductos.codigoDelProveedor") }}
              </th>
              <th v-if="modo === 'SIMPLE'" class="rth rth-nombre">
                {{ $t("importarProductos.producto") }}
              </th>
              <th v-if="modo === 'ESTRUCTURADO'" class="rth rth-nombre-est">
                {{ $t("importarProductos.producto") }}
              </th>
              <th class="rth rth-input">{{ $t("importarProductos.codigoPropio") }}</th>
              <th v-if="modo === 'ESTRUCTURADO'" class="rth rth-input">{{ $t("importarProductos.marca") }}</th>
              <th v-if="modo === 'ESTRUCTURADO'" class="rth rth-input">{{ $t("importarProductos.modelo") }}</th>
              <th v-if="modo === 'ESTRUCTURADO'" class="rth rth-input">{{ $t("importarProductos.tamanio") }}</th>
              <th v-if="modo === 'ESTRUCTURADO'" class="rth rth-input">{{ $t("producto.labels.otrasCaracteristicas") }}</th>
              <th class="rth rth-input">{{ $t("importarProductos.precioCompra") }}</th>
              <th class="rth rth-input-sm">Desc. %</th>
              <th class="rth rth-input-sm">IVA %</th>
              <th class="rth rth-input-sm">Margen %</th>
              <th class="rth rth-input">{{ $t("importarProductos.precioVenta") }}</th>
              <th class="rth rth-actions">{{ $t("common.actions") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(producto, index) in productosConPrecio"
              :key="index"
              class="rev-row"
              :class="{
                'row-error':   validacion && filaEstado(index) === 'error',
                'row-warning': validacion && filaEstado(index) === 'warning'
              }"
            >
              <!-- Estado -->
              <td class="rtd rtd-estado">
                <div v-if="validacion" class="estado-inner">
                  <span
                    class="estado-badge"
                    :class="{
                      'estado-badge--error':   filaEstado(index) === 'error',
                      'estado-badge--warning': filaEstado(index) === 'warning',
                      'estado-badge--ok':      filaEstado(index) === 'ok'
                    }"
                  >
                    {{ filaEstado(index) === 'error' ? 'Error' : filaEstado(index) === 'warning' ? 'Aviso' : 'OK' }}
                  </span>
                  <v-tooltip
                    v-if="erroresFila(index).length"
                    bottom
                    max-width="360"
                    open-delay="200"
                    content-class="importar-fila-tooltip"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="error"
                        class="estado-icon"
                        tabindex="0"
                        :aria-label="$t('importarProductos.verDetalleErrores')"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-alert-circle-outline</v-icon>
                    </template>
                    <div class="tooltip-mensajes-fila">
                      <div
                        v-for="(msg, ei) in erroresFila(index)"
                        :key="'err-' + index + '-' + ei"
                        class="tooltip-msg-line"
                      >{{ msg }}</div>
                    </div>
                  </v-tooltip>
                  <v-tooltip
                    v-else-if="advertenciasFila(index).length && filaEstado(index) === 'warning'"
                    bottom
                    max-width="360"
                    open-delay="200"
                    content-class="importar-fila-tooltip"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="warning darken-2"
                        class="estado-icon"
                        tabindex="0"
                        :aria-label="$t('importarProductos.verDetalleAvisos')"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-information-outline</v-icon>
                    </template>
                    <div class="tooltip-mensajes-fila">
                      <div
                        v-for="(msg, ai) in advertenciasFila(index)"
                        :key="'adv-' + index + '-' + ai"
                        class="tooltip-msg-line"
                      >{{ msg }}</div>
                    </div>
                  </v-tooltip>
                </div>
              </td>

              <!-- Codigo proveedor (condicional) -->
              <td v-if="mostrarColCodigoProveedor" class="rtd rtd-mono">
                {{ getCodigoProveedorProducto(producto) || "—" }}
              </td>

              <!-- Nombre SIMPLE (editable) -->
              <td v-if="modo === 'SIMPLE'" class="rtd rtd-editable rtd-nombre">
                <v-text-field
                  v-model="producto.nombre"
                  dense
                  solo
                  flat
                  hide-details
                  class="table-input-producto"
                  :placeholder="$t('importarProductos.producto')"
                />
              </td>

              <!-- Nombre ESTRUCTURADO (read-only resumen) -->
              <td v-if="modo === 'ESTRUCTURADO'" class="rtd rtd-nombre-est">
                {{ producto.nombre || "—" }}
              </td>

              <!-- Codigo propio -->
              <td class="rtd rtd-editable">
                <v-text-field
                  :value="getCodigoPropio(index)"
                  dense
                  hide-details
                  solo
                  flat
                  placeholder="..."
                  class="codigo-propio-cell"
                  @input="onCodigoPropioChange(index, $event)"
                />
              </td>

              <!-- Marca ESTRUCTURADO -->
              <td v-if="modo === 'ESTRUCTURADO'" class="rtd rtd-editable">
                <template v-if="producto.marca">
                  <div class="cell-flex-wrapper">
                    <v-autocomplete
                      v-if="mostrarAutocompleteMarca(index)"
                      :value="producto.marca"
                      :items="getMarcasItemsParaIndex(index)"
                      item-text="nombre"
                      item-value="nombre"
                      dense
                      hide-details
                      solo
                      flat
                      hide-no-data
                      :loading="loadingMarcas"
                      class="autocomplete-cell"
                      @input="onMarcaChange(index, $event)"
                    />
                    <span v-else class="cell-text">{{ producto.marca }}</span>
                    <span v-if="marcaEsNuevaSegunApi(index)" class="nuevo-badge">NUEVO</span>
                  </div>
                </template>
                <span v-else class="rtd-empty">—</span>
              </td>

              <!-- Modelo ESTRUCTURADO -->
              <td v-if="modo === 'ESTRUCTURADO'" class="rtd rtd-editable">
                <template v-if="producto.modelo">
                  <div class="cell-flex-wrapper">
                    <v-autocomplete
                      v-if="mostrarAutocompleteModelo(index)"
                      :value="producto.modelo"
                      :items="getModelosItemsParaIndex(index)"
                      item-text="nombre"
                      item-value="nombre"
                      dense
                      hide-details
                      solo
                      flat
                      hide-no-data
                      :loading="isLoadingModelosIndex(index)"
                      class="autocomplete-cell"
                      @input="onModeloChange(index, $event)"
                    />
                    <span v-else class="cell-text">{{ producto.modelo }}</span>
                    <span v-if="modeloEsNuevoSegunApi(index)" class="nuevo-badge">NUEVO</span>
                  </div>
                </template>
                <span v-else class="rtd-empty">—</span>
              </td>

              <!-- Tamanio ESTRUCTURADO -->
              <td v-if="modo === 'ESTRUCTURADO'" class="rtd rtd-editable">
                <template v-if="producto.tamanio">
                  <div class="cell-flex-wrapper">
                    <v-autocomplete
                      v-if="mostrarAutocompleteTamanio(index)"
                      :value="producto.tamanio"
                      :items="getTamaniosItemsParaIndex(index)"
                      item-text="tamanio"
                      item-value="tamanio"
                      dense
                      hide-details
                      solo
                      flat
                      hide-no-data
                      :loading="loadingTamanios"
                      class="autocomplete-cell"
                      @input="onTamanioChange(index, $event)"
                    />
                    <span v-else class="cell-text">{{ producto.tamanio }}</span>
                    <span v-if="tamanioEsNuevoSegunApi(index)" class="nuevo-badge">NUEVO</span>
                  </div>
                </template>
                <span v-else class="rtd-empty">—</span>
              </td>

              <!-- Otras caracteristicas ESTRUCTURADO -->
              <td v-if="modo === 'ESTRUCTURADO'" class="rtd rtd-editable">
                <v-text-field
                  v-model="producto.otrasCaracteristicas"
                  dense
                  solo
                  flat
                  hide-details
                  class="table-input-otras"
                  :placeholder="$t('producto.labels.otrasCaracteristicas')"
                />
              </td>

              <!-- Precio compra -->
              <td class="rtd rtd-editable">
                <v-text-field
                  v-model.number="producto.precioCompra"
                  type="number"
                  :min="0"
                  step="0.01"
                  dense
                  solo
                  flat
                  hide-details
                  class="table-input"
                  prefix="$"
                  @input="resetPrecioManual(producto)"
                />
              </td>

              <!-- Descuento % -->
              <td class="rtd rtd-editable">
                <v-text-field
                  v-model.number="producto.descuentoProveedor"
                  type="number"
                  :min="0"
                  :max="100"
                  dense
                  solo
                  flat
                  hide-details
                  class="table-input-small"
                  suffix="%"
                  @input="resetPrecioManual(producto)"
                />
              </td>

              <!-- IVA % -->
              <td class="rtd rtd-editable">
                <v-select
                  v-model.number="producto.ivaPorcentaje"
                  :items="ivaOptions"
                  dense
                  solo
                  flat
                  hide-details
                  class="table-input"
                  @change="resetPrecioManual(producto)"
                />
              </td>

              <!-- Margen % -->
              <td class="rtd rtd-editable">
                <v-text-field
                  v-model.number="producto.porcentajeSobreCosto"
                  type="number"
                  :min="0"
                  dense
                  solo
                  flat
                  hide-details
                  class="table-input-small"
                  suffix="%"
                  @input="resetPrecioManual(producto)"
                />
              </td>

              <!-- Precio venta -->
              <td class="rtd rtd-editable">
                <v-text-field
                  v-model.number="producto.precioVentaNuevo"
                  type="number"
                  :min="0"
                  step="0.01"
                  dense
                  solo
                  flat
                  hide-details
                  class="table-input table-input-precio-venta"
                  prefix="$"
                  :placeholder="formatCurrency(calcularPrecioAutomatico(producto))"
                />
              </td>

              <!-- Acciones -->
              <td class="rtd rtd-actions">
                <button class="del-btn" :title="$t('common.delete')" @click="removeProducto(index)">
                  <v-icon small>mdi-delete-outline</v-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-fade-right"></div>
    </div>

  </div>
</template>

<script>
import MarcaProvider from "../../../marca/providers/MarcaProvider";
import ModeloProvider from "../../../modelo/providers/ModeloProvider";
import TamanioProvider from "../../../tamanio/providers/TamanioProvider";
import { generarCodigoBarrasPropio, OPCIONES_MODO_CODIGO_BARRAS } from "../../utils/codigoBarrasUtils";

export default {
  name: "PasoCalculoPrecios",
  props: {
    productos: { type: Array, default: () => [] },
    modo: { type: String, required: true },
    validacion: { type: Object, default: null },
    modoCodigoBarras: { type: String, default: "autogenerar" },
  },
  data() {
    return {
      valid: false,
      productosConPrecio: [],
      overrides: {},
      descuentoProveedorGlobal: 0,
      ivaPorcentajeGlobal: 21,
      porcentajeSobreCostoGlobal: 0,
      tipoRedondeoGlobal: "ninguno",
      opcionesModoCodigoBarras: OPCIONES_MODO_CODIGO_BARRAS,
      marcasItems: [],
      modelosPorMarca: {},
      tamaniosItems: [],
      loadingMarcas: false,
      loadingModelos: {},
      loadingTamanios: false,
      ivaOptions: [
        { text: "0%", value: 0 },
        { text: "10.5%", value: 10.5 },
        { text: "21%", value: 21 },
        { text: "27%", value: 27 },
      ],
      opcionesRedondeo: [
        { text: "Sin redondeo", value: "ninguno" },
        { text: "Redondear centavos", value: "centavos" },
        { text: "Redondear a multiplo de 10", value: "multiplo_10" },
        { text: "Redondear a multiplo de 100", value: "multiplo_100" },
      ],
    };
  },
  computed: {
    mostrarColCodigoProveedor() {
      return (this.productosConPrecio || []).some(
        (p) => p?.codigoBarrasProveedor != null && String(p.codigoBarrasProveedor).trim() !== ""
      );
    },
    totalErrores() {
      return this.validacion?.totalErrores ?? 0;
    },
    totalAdvertencias() {
      return this.validacion?.totalAdvertencias ?? 0;
    },
  },
  watch: {
    productos: {
      immediate: true,
      handler(rows) {
        if (!rows || rows.length === 0) {
          if (this.productosConPrecio.length > 0) {
            return;
          }
          this.overrides = {};
          this.productosConPrecio = [];
          return;
        }
        this.overrides = {};
        this.productosConPrecio = rows.map((r) => ({
          ...r,
          descuentoProveedor: r.descuentoProveedor != null && r.descuentoProveedor !== "" ? Number(r.descuentoProveedor) : (this.descuentoProveedorGlobal ?? 0),
          ivaPorcentaje: this.ivaPorcentajeGlobal ?? 21,
          porcentajeSobreCosto: this.porcentajeSobreCostoGlobal ?? 0,
          tipoRedondeo: this.tipoRedondeoGlobal || "ninguno",
          precioVentaNuevo: null,
        }));
      },
    },
    marcasItems: {
      handler() {
        this.cargarModelosParaFilasConMarca();
      },
      deep: true,
    },
    productosConPrecio: {
      handler() {
        this.cargarModelosParaFilasConMarca();
      },
      deep: true,
    },
    descuentoProveedorGlobal(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productosConPrecio.forEach((p) => { p.descuentoProveedor = newVal; });
      }
    },
    ivaPorcentajeGlobal(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productosConPrecio.forEach((p) => { p.ivaPorcentaje = newVal; });
      }
    },
    porcentajeSobreCostoGlobal(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productosConPrecio.forEach((p) => { p.porcentajeSobreCosto = newVal; });
      }
    },
    tipoRedondeoGlobal(newVal) {
      this.productosConPrecio.forEach((p) => { p.tipoRedondeo = newVal || "ninguno"; });
    },
  },
  mounted() {
    this.cargarMarcas();
    this.cargarTamanios();
  },
  methods: {
    getFilaValidacion(index) {
      const n = index + 1;
      const filas = this.validacion?.filas;
      if (!filas || !filas.length) return null;
      return filas.find((x) => x.fila === n) || filas[index] || null;
    },
    filaEstado(index) {
      if (!this.validacion) return null;
      return this.getFilaValidacion(index)?.estado || "ok";
    },
    erroresFila(index) {
      if (!this.validacion) return [];
      const raw = this.getFilaValidacion(index)?.errores;
      if (raw == null) return [];
      return Array.isArray(raw) ? raw : [String(raw)];
    },
    advertenciasFila(index) {
      if (!this.validacion) return [];
      const raw = this.getFilaValidacion(index)?.advertencias;
      if (raw == null) return [];
      return Array.isArray(raw) ? raw : [String(raw)];
    },
    marcaEsNuevaSegunApi(index) {
      const v = this.getFilaValidacion(index);
      return !!(this.validacion && v && v.marcaExiste === false);
    },
    mostrarAutocompleteMarca(index) {
      return !this.validacion || this.marcaEsNuevaSegunApi(index);
    },
    modeloEsNuevoSegunApi(index) {
      const v = this.getFilaValidacion(index);
      return !!(this.validacion && v && v.modeloExiste === false);
    },
    mostrarAutocompleteModelo(index) {
      return !this.validacion || this.modeloEsNuevoSegunApi(index);
    },
    tamanioEsNuevoSegunApi(index) {
      const v = this.getFilaValidacion(index);
      return !!(this.validacion && v && v.tamanioExiste === false);
    },
    mostrarAutocompleteTamanio(index) {
      return !this.validacion || this.tamanioEsNuevoSegunApi(index);
    },
    async cargarMarcas() {
      this.loadingMarcas = true;
      try {
        const r = await MarcaProvider.fetchMarcas();
        this.marcasItems = r?.data?.marcaFetch || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingMarcas = false;
      }
    },
    async cargarModelosPorMarca(marcaId) {
      if (!marcaId || this.modelosPorMarca[marcaId]) return;
      this.$set(this.loadingModelos, marcaId, true);
      try {
        const r = await ModeloProvider.fetchModelos(marcaId);
        this.$set(this.modelosPorMarca, marcaId, r?.data?.modeloFetch || []);
      } catch (e) {
        console.error(e);
        this.$set(this.modelosPorMarca, marcaId, []);
      } finally {
        this.$set(this.loadingModelos, marcaId, false);
      }
    },
    async cargarModelosParaFilasConMarca() {
      if (!this.marcasItems.length || !this.productosConPrecio.length) return;
      const marcasIds = new Set();
      this.productosConPrecio.forEach((p, i) => {
        if (!p.marca) return;
        const v = this.getFilaValidacion(i);
        const cargar = !this.validacion || (v && v.modeloExiste === false);
        if (cargar) {
          const marca = this.marcasItems.find(
            (m) => m.nombre === p.marca || (m.nombre && m.nombre.toLowerCase() === (p.marca || "").toLowerCase())
          );
          if (marca?.id) marcasIds.add(marca.id);
        }
      });
      for (const id of marcasIds) {
        await this.cargarModelosPorMarca(id);
      }
    },
    async cargarTamanios() {
      this.loadingTamanios = true;
      try {
        const r = await TamanioProvider.fetchTamanios();
        this.tamaniosItems = r?.data?.tamanioFetch || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingTamanios = false;
      }
    },
    getMarcaIdParaIndex(index) {
      const p = this.productosConPrecio[index];
      if (!p?.marca) return null;
      const marca = this.marcasItems.find(
        (m) => m.nombre === p.marca || (m.nombre && m.nombre.toLowerCase() === (p.marca || "").toLowerCase())
      );
      return marca ? marca.id : null;
    },
    getMarcasItemsParaIndex(index) {
      const p = this.productosConPrecio[index];
      const val = p?.marca || "";
      if (!val || this.marcasItems.some((m) => m.nombre === val)) return this.marcasItems;
      return [{ nombre: val }, ...this.marcasItems];
    },
    getModelosItemsParaIndex(index) {
      const marcaId = this.getMarcaIdParaIndex(index);
      const modelos = marcaId ? (this.modelosPorMarca[marcaId] || []) : [];
      const p = this.productosConPrecio[index];
      const val = p?.modelo || "";
      if (!val || modelos.some((m) => m.nombre === val)) return modelos;
      return [{ nombre: val }, ...modelos];
    },
    isLoadingModelosIndex(index) {
      const marcaId = this.getMarcaIdParaIndex(index);
      return marcaId ? this.loadingModelos[marcaId] : false;
    },
    getTamaniosItemsParaIndex(index) {
      const p = this.productosConPrecio[index];
      const val = p?.tamanio || "";
      if (!val || this.tamaniosItems.some((t) => t.tamanio === val)) return this.tamaniosItems;
      return [{ tamanio: val }, ...this.tamaniosItems];
    },
    onMarcaChange(index, value) {
      this.$set(this.productosConPrecio[index], "marca", value);
      const marca = this.marcasItems.find((m) => m.nombre === value);
      if (marca?.id) this.cargarModelosPorMarca(marca.id);
    },
    onModeloChange(index, value) {
      this.$set(this.productosConPrecio[index], "modelo", value);
    },
    onTamanioChange(index, value) {
      this.$set(this.productosConPrecio[index], "tamanio", value);
    },
    onModoCodigoBarras(v) {
      this.$emit("update:modoCodigoBarras", v);
    },
    getCodigoProveedorProducto(p) {
      if (p?.codigoBarrasProveedor != null && String(p.codigoBarrasProveedor).trim() !== "") {
        return String(p.codigoBarrasProveedor).trim();
      }
      return "";
    },
    getCodigoPropio(index) {
      const fila = index + 1;
      const override = this.overrides[fila]?.codigoBarrasPropio;
      if (override !== undefined) return override;
      const p = this.productosConPrecio[index];
      if (!p) return "";
      if (p.codigoBarrasPropio !== undefined && p.codigoBarrasPropio !== null) return p.codigoBarrasPropio;
      const codigoProveedor = this.getCodigoProveedorProducto(p) || "";
      if (this.modoCodigoBarras === "usarProveedor") return codigoProveedor || "";
      if (this.modoCodigoBarras === "manual") return "";
      const modelo = this.modo === "SIMPLE" ? p.nombre : p.modelo;
      return generarCodigoBarrasPropio(p.marca, p.tamanio, codigoProveedor, modelo) || "";
    },
    onCodigoPropioChange(index, value) {
      this.$set(this.overrides, index + 1, { ...this.overrides[index + 1], codigoBarrasPropio: value });
    },
    calcularPrecioAutomatico(producto) {
      if (!producto.precioCompra || producto.precioCompra <= 0) return 0;
      let precio = producto.precioCompra * (1 - (producto.descuentoProveedor || 0) / 100);
      precio = precio * (1 + (producto.ivaPorcentaje || 0) / 100);
      precio = precio * (1 + (producto.porcentajeSobreCosto || 0) / 100);
      const tipoRedondeo = producto.tipoRedondeo || "ninguno";
      if (precio > 0 && tipoRedondeo !== "ninguno") {
        switch (tipoRedondeo) {
          case "centavos": precio = Math.ceil(precio); break;
          case "multiplo_10": precio = Math.ceil(precio / 10) * 10; break;
          case "multiplo_100": precio = Math.ceil(precio / 100) * 100; break;
        }
      }
      return parseFloat(precio.toFixed(2));
    },
    calcularPrecioVentaNuevo(producto) {
      if (producto.precioVentaNuevo !== null && producto.precioVentaNuevo !== undefined && producto.precioVentaNuevo !== "") {
        return parseFloat(producto.precioVentaNuevo);
      }
      return this.calcularPrecioAutomatico(producto);
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },
    resetPrecioManual(producto) {
      producto.precioVentaNuevo = null;
    },
    removeProducto(index) {
      this.productosConPrecio.splice(index, 1);
      this.$emit("remove", { filaIndex: index });
    },
    codigoBarrasPropioParaEnvio(index) {
      const fila = index + 1;
      const o = this.overrides[fila]?.codigoBarrasPropio;
      if (o !== undefined) return o;
      const p = this.productosConPrecio[index];
      return p?.codigoBarrasPropio;
    },
    getProductosParaEnviar() {
      return this.productosConPrecio.map((p, index) => {
        const precioCompra = parseFloat(p.precioCompra) || 0;
        let precioVenta = this.calcularPrecioVentaNuevo(p);
        if (typeof precioVenta !== "number" || isNaN(precioVenta) || precioVenta <= 0) {
          precioVenta = precioCompra > 0 ? precioCompra : 0;
        }
        const codPropio = this.codigoBarrasPropioParaEnvio(index);
        return {
          codigoBarras: p.codigoBarras,
          codigoBarrasProveedor: p.codigoBarrasProveedor,
          codigoBarrasPropio: codPropio !== undefined ? codPropio : undefined,
          nombre: p.nombre,
          marca: p.marca,
          modelo: p.modelo,
          tamanio: p.tamanio,
          otrasCaracteristicas: p.otrasCaracteristicas,
          precioCompra,
          stock: 0,
          stockAlerta: 0,
          descuentoProveedor: p.descuentoProveedor ?? 0,
          ivaPorcentaje: p.ivaPorcentaje ?? 0,
          porcentajeSobreCosto: p.porcentajeSobreCosto ?? 0,
          precioVenta,
          tipoRedondeo: p.tipoRedondeo || "ninguno",
        };
      });
    },
  },
};
</script>

<style scoped>
/* ========================
   GLOBAL CONTROLS BAR
======================== */
.gc-bar {
  padding: 12px 20px 14px;
  background: #FAFAFA;
  border-bottom: 1px solid #F0F0F0;
}
.gc-controls {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}
.gc-control-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 130px;
}
.gc-control-wrap--lg {
  min-width: 220px;
}
.gc-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #5F6368;
  cursor: default;
}
.gc-select,
.gc-input {
  max-width: 100%;
}
.gc-select--sm,
.gc-input--sm {
  max-width: 110px;
}

/* ========================
   VALIDATION SUMMARY
======================== */
.val-summary {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  font-size: 13px;
  border-bottom: 1px solid #F0F0F0;
}
.val-summary--error {
  background: #FFEBEE;
  color: #B71C1C;
}
.val-summary--warn {
  background: #FFF8E1;
  color: #E65100;
}
.val-summary--ok {
  background: #F1F8E9;
  color: #2E7D32;
}

/* ========================
   EMPTY STATE
======================== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 12px;
}
.empty-text {
  font-size: 14px;
  color: #9E9E9E;
  margin: 0;
}

/* ========================
   TABLE WRAPPER
======================== */
.table-wrapper {
  position: relative;
  border-top: 1px solid #E8EAED;
  overflow: hidden;
}
.table-scroll {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 520px;
}
.table-fade-right {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 32px;
  background: linear-gradient(to right, transparent, rgba(255,255,255,0.9));
  pointer-events: none;
}

/* ========================
   CUSTOM TABLE
======================== */
.rev-table {
  border-collapse: collapse;
  width: 100%;
  min-width: 900px;
  font-size: 13px;
}

/* Headers */
.rth {
  padding: 9px 10px;
  background: #F8F9FA;
  border-bottom: 2px solid #E8EAED;
  border-right: 1px solid #F0F0F0;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #5F6368;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
}
.rth-fixed  { width: 100px; min-width: 90px; }
.rth-nombre { min-width: 200px; width: 22%; }
.rth-nombre-est { min-width: 140px; max-width: 180px; }
.rth-input  { min-width: 110px; }
.rth-input-sm { min-width: 80px; }
.rth-actions { width: 52px; text-align: center; }

/* Data rows */
.rev-row {
  transition: filter 0.1s;
}
.rev-row:hover td {
  filter: brightness(0.97);
}
.row-error td  { background: #FFF5F5; }
.row-warning td { background: #FFFDE7; }

/* Data cells */
.rtd {
  padding: 6px 10px;
  border-bottom: 1px solid #F5F5F5;
  border-right: 1px solid #F0F0F0;
  vertical-align: middle;
}
.rtd-editable { padding: 3px 6px; }
.rtd-estado   { width: 100px; min-width: 90px; }
.rtd-mono     { font-family: monospace; font-size: 11.5px; color: #5F6368; min-width: 100px; max-width: 130px; }
.rtd-nombre   { min-width: 200px; }
.rtd-nombre-est { max-width: 160px; word-break: break-word; font-size: 12px; color: #5F6368; }
.rtd-actions  { text-align: center; width: 52px; }
.rtd-empty    { color: #BDBDBD; }

/* Estado badge */
.estado-inner {
  display: flex;
  align-items: center;
  gap: 5px;
}
.estado-badge {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.2px;
  flex-shrink: 0;
}
.estado-badge--error {
  background: #FFEBEE;
  color: #B71C1C;
  border: 1px solid #FFCDD2;
}
.estado-badge--warning {
  background: #FFF8E1;
  color: #E65100;
  border: 1px solid #FFE0B2;
}
.estado-badge--ok {
  background: #E8F5E9;
  color: #1B5E20;
  border: 1px solid #C8E6C9;
}
.estado-icon {
  flex-shrink: 0;
  cursor: help;
  outline: none;
}

/* Cell flex wrapper (marca/modelo/tamanio) */
.cell-flex-wrapper {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: nowrap;
}
.cell-text {
  font-size: 13px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #202124;
}
.nuevo-badge {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  background: #E3F2FD;
  color: #1565C0;
  border: 1px solid #BBDEFB;
  flex-shrink: 0;
  letter-spacing: 0.3px;
}

/* Delete button */
.del-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #E0E0E0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  color: #9E9E9E;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.del-btn:hover {
  background: #FFEBEE;
  border-color: #EF9A9A;
  color: #C62828;
}

/* ========================
   INPUT FIELDS (Vuetify overrides)
======================== */
.autocomplete-cell {
  max-width: 140px;
  min-width: 70px;
  flex: 0 0 auto;
}
.codigo-propio-cell {
  max-width: 200px;
  min-width: 100px;
  flex: 0 0 auto;
}
::v-deep .autocomplete-cell .v-input__control,
::v-deep .autocomplete-cell .v-input__slot,
::v-deep .codigo-propio-cell .v-input__control,
::v-deep .codigo-propio-cell .v-input__slot {
  min-height: 32px !important;
  padding-left: 0 !important;
}
::v-deep .autocomplete-cell .v-select__selection { max-width: 70px; }
::v-deep .codigo-propio-cell .v-input__slot,
::v-deep .autocomplete-cell .v-input__slot {
  background-color: #ffffff !important;
  border: 2px solid #7986CB !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
::v-deep .codigo-propio-cell .v-input__slot:hover,
::v-deep .autocomplete-cell .v-input__slot:hover {
  border-color: #5C6BC0 !important;
  box-shadow: 0 2px 6px rgba(63,81,181,0.15);
}
::v-deep .codigo-propio-cell.v-input--is-focused .v-input__slot,
::v-deep .autocomplete-cell.v-input--is-focused .v-input__slot {
  background-color: #E8EAF6 !important;
  border-color: #3F51B5 !important;
  box-shadow: 0 0 0 3px rgba(63,81,181,0.2), 0 2px 8px rgba(63,81,181,0.25);
}
::v-deep .codigo-propio-cell input,
::v-deep .autocomplete-cell .v-select__selection {
  color: #1A237E;
  font-weight: 600;
}
::v-deep .table-input         { max-width: 100px; }
::v-deep .table-input-precio-venta { max-width: 140px; }
::v-deep .table-input-small   { max-width: 80px; }
::v-deep .table-input-otras   { max-width: 140px; }
::v-deep .table-input-producto { max-width: 100%; min-width: 180px; }
::v-deep .table-input .v-input__control,
::v-deep .table-input-precio-venta .v-input__control,
::v-deep .table-input-small .v-input__control,
::v-deep .table-input-otras .v-input__control,
::v-deep .table-input-producto .v-input__control { min-height: 32px !important; }
::v-deep .table-input .v-input__slot,
::v-deep .table-input-precio-venta .v-input__slot,
::v-deep .table-input-small .v-input__slot,
::v-deep .table-input-otras .v-input__slot,
::v-deep .table-input-producto .v-input__slot {
  background-color: #ffffff !important;
  border: 2px solid #7986CB !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
::v-deep .table-input .v-input__slot:hover,
::v-deep .table-input-precio-venta .v-input__slot:hover,
::v-deep .table-input-small .v-input__slot:hover,
::v-deep .table-input-otras .v-input__slot:hover,
::v-deep .table-input-producto .v-input__slot:hover {
  border-color: #5C6BC0 !important;
  box-shadow: 0 2px 6px rgba(63,81,181,0.15);
}
::v-deep .table-input.v-input--is-focused .v-input__slot,
::v-deep .table-input-precio-venta.v-input--is-focused .v-input__slot,
::v-deep .table-input-small.v-input--is-focused .v-input__slot,
::v-deep .table-input-otras.v-input--is-focused .v-input__slot,
::v-deep .table-input-producto.v-input--is-focused .v-input__slot {
  background-color: #E8EAF6 !important;
  border-color: #3F51B5 !important;
  box-shadow: 0 0 0 3px rgba(63,81,181,0.2), 0 2px 8px rgba(63,81,181,0.25);
}
::v-deep .table-input input[type="number"],
::v-deep .table-input-precio-venta input[type="number"],
::v-deep .table-input-small input[type="number"] {
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  color: #1A237E;
}
::v-deep .table-input input[type="number"]::-webkit-inner-spin-button,
::v-deep .table-input input[type="number"]::-webkit-outer-spin-button,
::v-deep .table-input-precio-venta input[type="number"]::-webkit-inner-spin-button,
::v-deep .table-input-precio-venta input[type="number"]::-webkit-outer-spin-button,
::v-deep .table-input-small input[type="number"]::-webkit-inner-spin-button,
::v-deep .table-input-small input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
::v-deep .table-input input[type="number"],
::v-deep .table-input-precio-venta input[type="number"],
::v-deep .table-input-small input[type="number"] {
  -moz-appearance: textfield;
}
::v-deep .table-input .v-select__selection {
  color: #1A237E;
  font-weight: 600;
}
/* Global control selects/inputs — compact */
::v-deep .gc-select .v-input__slot,
::v-deep .gc-input .v-input__slot {
  min-height: 36px !important;
}
::v-deep .gc-select .v-input__control,
::v-deep .gc-input .v-input__control {
  min-height: 36px !important;
}
</style>

<!-- Tooltip se monta en el body -->
<style>
.importar-fila-tooltip {
  font-size: 13px !important;
  line-height: 1.45;
  padding: 10px 14px !important;
  text-align: left;
}
.importar-fila-tooltip .tooltip-msg-line + .tooltip-msg-line {
  margin-top: 10px;
}
</style>
